import { useGeneralSettings } from '../useGeneralSettings'

/* ===================== SUPPORT ===================== */
/** Non-empty trimmed string, else '' — the API sends empty strings for unset fields. */
const val = (v) => (typeof v === 'string' && v.trim() ? v.trim() : '')

/** wa.me/tel need digits; bare 10-digit Indian numbers get the 91 prefix. */
const toDigits = (raw) => {
  const digits = raw.replace(/\D/g, '')
  return digits.length === 10 ? `91${digits}` : digits
}
const FAQ = [
  ['How do I reschedule a consultation?', 'Open the Consultations tab, find your booking and tap Reschedule. You can move it up to 24 hours before the slot.'],
  ['When will my order ship?', 'Energised items ship within 2–3 working days. Track status anytime under the Orders tab.'],
  ['Is my birth data private?', 'Yes — your details are encrypted and used only to prepare your readings.'],
]
const TICKETS = [
  { icon: '🎫', name: 'Gemstone order delay', meta: '#TK-3092 · 26 Jun 2026', status: 'Open', cls: 'bg-amber-50 text-amber-600', bg: 'bg-amber-50' },
  { icon: '🎫', name: 'Course video not loading', meta: '#TK-3041 · 19 Jun 2026', status: 'Resolved', cls: 'bg-green-50 text-green-600', bg: 'bg-stone-100' },
]

export default function SupportTab({ data } = {}) {
  const firstName = (data?.profile?.name || 'Seeker').split(/\s+/)[0]
  const { settings } = useGeneralSettings()

  // Live contact details with the tab's previous copy as per-field fallback —
  // same idiom as the website ContactPage.
  const phone = val(settings.phone) || '+91 98765 43210'
  const email = val(settings.email) || 'info@astrorajumaharaj.com'
  const channels = [
    { accent: '#16a34a', bg: 'linear-gradient(135deg,#dcfce7,#bbf7d0)', icon: '💬', title: 'WhatsApp Chat', sub: 'Fastest response', href: `https://wa.me/${toDigits(val(settings.whatsapp) || phone)}`, external: true },
    { accent: '#D97706', bg: 'linear-gradient(135deg,#FEF3C7,#FDE68A)', icon: '📞', title: 'Call Support', sub: phone, href: `tel:+${toDigits(phone)}` },
    { accent: '#2563eb', bg: 'linear-gradient(135deg,#dbeafe,#bfdbfe)', icon: '✉️', title: 'Email Us', sub: 'Reply within 24h', href: `mailto:${email}` },
    // Opens the FloatingFabs assistant panel — the widget listens for this
    // window event (no prop path from the dashboard into it).
    { accent: '#9333ea', bg: 'linear-gradient(135deg,#f3e8ff,#e9d5ff)', icon: '🧑‍💻', title: 'Live Chat', sub: 'Avg wait · 2 min', onClick: () => window.dispatchEvent(new Event('astro:open-live-chat')) },
  ]
  return (
    <div className="space-y-6">
      <div className="dash-card p-4 sm:p-8 bg-gradient-to-br from-amber-50 to-orange-50/50 border border-amber-100 relative overflow-hidden">
        <div className="absolute -top-12 -right-12 w-40 h-40 rounded-full bg-amber-200/30 blur-2xl" />
        <div className="relative z-10 max-w-xl">
          <span className="eyebrow text-amber-700">We're Here For You</span>
          <h3 className="text-2xl sm:text-3xl text-astroDark mt-2 font-royal">How can we help, {firstName}?</h3>
          <p className="text-sm text-stone-500 font-light mt-3 leading-relaxed">Our support team is available Monday to Saturday, 9 AM – 7 PM IST. Choose a channel below or raise a ticket and we'll respond within a few hours.</p>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {channels.map((c) => {
          const inner = (
            <>
              <div className="stat-icon" style={{ background: c.bg }}>{c.icon}</div>
              <p className="text-sm font-semibold text-astroDark mt-4">{c.title}</p>
              <p className="text-[11px] text-stone-500 mt-1">{c.sub}</p>
            </>
          )
          return c.href ? (
            <a
              key={c.title}
              href={c.href}
              {...(c.external ? { target: '_blank', rel: 'noopener' } : {})}
              className="stat-card block"
              style={{ '--accent': c.accent }}
            >
              {inner}
            </a>
          ) : (
            <button
              key={c.title}
              type="button"
              onClick={c.onClick}
              className="stat-card block w-full text-left cursor-pointer"
              style={{ '--accent': c.accent }}
            >
              {inner}
            </button>
          )
        })}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        <div className="lg:col-span-3 dash-card p-4 sm:p-7">
          <h3 className="text-lg text-astroDark font-royal">Raise a Support Ticket</h3>
          <p className="text-xs text-stone-500 mt-1 mb-5">Describe your issue and our team will get back to you by email.</p>
          <form onSubmit={(e) => e.preventDefault()} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-[9px] font-bold uppercase tracking-[0.15em] text-stone-600 mb-1.5">Topic</label>
                <select className="w-full bg-stone-50 border border-stone-200 rounded-xl px-4 py-3 text-sm text-astroDark focus:outline-none focus:border-goldAccent/50 focus:bg-white transition-colors">
                  <option>Consultation / Booking</option><option>Shop Order or Delivery</option><option>Course Access</option><option>Payments</option><option>Account &amp; Login</option><option>Other</option>
                </select>
              </div>
              <div>
                <label className="block text-[9px] font-bold uppercase tracking-[0.15em] text-stone-600 mb-1.5">Priority</label>
                <select className="w-full bg-stone-50 border border-stone-200 rounded-xl px-4 py-3 text-sm text-astroDark focus:outline-none focus:border-goldAccent/50 focus:bg-white transition-colors">
                  <option>Normal</option><option>High</option><option>Urgent</option>
                </select>
              </div>
            </div>
            <div>
              <label className="block text-[9px] font-bold uppercase tracking-[0.15em] text-stone-600 mb-1.5">Subject</label>
              <input required placeholder="Brief summary of your issue" className="w-full bg-stone-50 border border-stone-200 rounded-xl px-4 py-3 text-sm text-astroDark focus:outline-none focus:border-goldAccent/50 focus:bg-white transition-colors" />
            </div>
            <div>
              <label className="block text-[9px] font-bold uppercase tracking-[0.15em] text-stone-600 mb-1.5">Message</label>
              <textarea rows="4" required placeholder="Tell us what's going on…" className="w-full bg-stone-50 border border-stone-200 rounded-xl px-4 py-3 text-sm text-astroDark focus:outline-none focus:border-goldAccent/50 focus:bg-white transition-colors" />
            </div>
            <button type="submit" className="shine-btn bg-gradient-to-r from-goldAccent to-goldPrimary text-astroDark font-bold text-[11px] uppercase tracking-[0.2em] px-6 py-3 rounded-xl shadow-md shadow-goldPrimary/10 transition-transform hover:-translate-y-0.5">Submit Ticket</button>
          </form>
        </div>

        <div className="lg:col-span-2 space-y-6">
          <div className="dash-card p-4 sm:p-6">
            <h3 className="text-lg text-astroDark mb-3 font-royal">Quick Answers</h3>
            <div className="divide-y divide-stone-100">
              {FAQ.map(([q, a]) => (
                <details key={q} className="py-3 group">
                  <summary className="flex items-center justify-between cursor-pointer list-none text-[13px] font-semibold text-astroDark">{q}<span className="text-goldAccent group-open:rotate-45 transition-transform text-lg leading-none">+</span></summary>
                  <p className="text-[12px] text-stone-500 font-light leading-relaxed mt-2">{a}</p>
                </details>
              ))}
            </div>
          </div>
          <div className="dash-card p-4 sm:p-6">
            <h3 className="text-lg text-astroDark mb-4 font-royal">Recent Tickets</h3>
            <div className="space-y-3">
              {TICKETS.map((t) => (
                <div key={t.name} className="flex items-center gap-3">
                  <span className={`w-9 h-9 rounded-lg flex items-center justify-center text-sm shrink-0 ${t.bg}`}>{t.icon}</span>
                  <div className="flex-1 min-w-0"><p className="text-xs font-semibold text-astroDark truncate">{t.name}</p><p className="text-[10px] text-stone-400">{t.meta}</p></div>
                  <span className={`text-[9px] font-bold uppercase px-2 py-1 rounded-full ${t.cls}`}>{t.status}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
